import { Component, type ErrorInfo, type ReactElement, type ReactNode } from 'react';
import { useT } from './i18n.jsx';

/**
 * Filet de sécurité d'affichage.
 *
 * Sans lui, une exception pendant le rendu démonte tout l'arbre React : la page
 * devient blanche, les menus disparaissent, et rien n'indique ce qui s'est
 * passé. C'est exactement ce qui est arrivé le 5 août 2026, parce qu'un score de
 * confiance arrivait en chaîne au lieu d'un nombre.
 *
 * Le vrai défaut a été corrigé à la source, côté pilote de base. Ce garde-fou
 * traite l'autre moitié du problème : une donnée inattendue doit dégrader
 * l'affichage, jamais l'effacer. Une page blanche ne dit rien à l'exploitant et
 * fait perdre du temps à celui qui diagnostique.
 */

interface Props {
  children: ReactNode;
}

interface State {
  message: string | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { message: null };

  static getDerivedStateFromError(error: unknown): State {
    return { message: error instanceof Error ? error.message : 'Erreur inconnue' };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Rendu interrompu', error, info.componentStack);
  }

  override render(): ReactNode {
    if (this.state.message === null) return this.props.children;

    return <EcranInterrompu message={this.state.message} />;
  }
}

/*
 * L'écran est un composant à part : `ErrorBoundary` doit rester une CLASSE pour
 * attraper l'exception, et une classe n'a pas accès aux hooks — donc pas à la
 * langue choisie. Séparer les deux est le seul moyen de traduire la page de
 * crash sans renoncer au garde-fou.
 */
function EcranInterrompu({ message }: { message: string }): ReactElement {
  const t = useT();
  return (
    <div className="login">
      <h1>{t('Affichage interrompu')}</h1>
      <p className="error">{message}</p>
      <p className="hint">
        {t(
          'Les données sont intactes : seule leur mise en forme a échoué. Recharger la page ; si l’erreur persiste, elle est reproductible et donc corrigeable.',
        )}
      </p>
      <button type="button" onClick={(): void => window.location.reload()}>
        {t('Recharger')}
      </button>
    </div>
  );
}
